import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Template as WysiwygTemplate, OutputFormat } from './wysiwyg/types';
import { getFormatChooserOptions } from './wysiwyg/formats/index';
import useBaseTemplates, { getBaseTemplateById } from './hooks/useBaseTemplates';
import type { BaseTemplate } from './types/baseTemplates';
import { prepareBaseTemplateSource } from './utils/baseTemplateSource';
import { validateTemplateForSave } from './wysiwyg/templateNormalization';

export type TemplateFormatSelection = {
  format: OutputFormat;
  dpi: number;
};

export type TemplateProvenance = { market: string; category: string };

type NewTemplateChooserModalProps = {
  organizationId: number | string | null;
  userId?: number | string | null;
  initialFormat?: TemplateFormatSelection;
  onSelectBlank: (formatSelection: TemplateFormatSelection) => void;
  onSelectTemplate: (template: WysiwygTemplate, cloneName: string, formatSelection: TemplateFormatSelection, marketIds: number[], provenance: TemplateProvenance) => void;
  onClose: () => void;
};

const getTemplateMetadata = (template: BaseTemplate) =>
  `${template.width} \u00d7 ${template.height} ${template.unit} \u00b7 ${template.qrCompatibility.toUpperCase()}`;

const titleContainsMetadata = (title: string, value: string) => {
  const escapedValue = value.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return escapedValue !== '' && new RegExp(`(^|[^a-z0-9])${escapedValue}(?=$|[^a-z0-9])`, 'i').test(title);
};

function FormatCard({ title, description, note, testId, onClick, recommended }: {
  title: string;
  description: string;
  note?: string;
  testId: string;
  onClick: () => void;
  recommended?: boolean;
}) {
  const defaultBorder = recommended ? '#2e7d5b' : 'var(--border, #e2e5e9)';
  const defaultBg = recommended ? '#f0f7f4' : '#fff';

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        flex: '1 1 160px',
        minWidth: 160,
        textAlign: 'center',
        padding: 20,
        borderRadius: 10,
        border: `2px solid ${defaultBorder}`,
        background: defaultBg,
        cursor: 'pointer',
        transition: 'border-color 120ms ease, background 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#213F39';
        e.currentTarget.style.background = '#eef2f5';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = defaultBorder;
        e.currentTarget.style.background = defaultBg;
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{title}</div>
      <div className="muted" style={{ fontSize: 12 }}>{description}</div>
      {note && (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          {note}
        </div>
      )}
    </button>
  );
}

export default function NewTemplateChooserModal({
  organizationId,
  userId,
  initialFormat,
  onSelectBlank,
  onSelectTemplate,
  onClose,
}: NewTemplateChooserModalProps) {
  const [step, setStep] = useState<'format' | 'source'>(initialFormat ? 'source' : 'format');
  const [selectedFormat, setSelectedFormat] = useState<TemplateFormatSelection | null>(initialFormat ?? null);
  const [allTemplatesMarketId, setAllTemplatesMarketId] = useState<number | null>(null);
  const [selectingTemplateId, setSelectingTemplateId] = useState<number | null>(null);
  const [selectionError, setSelectionError] = useState('');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const selectionRequestId = useRef(0);
  const onCloseRef = useRef(onClose);
  const contextKey = `${organizationId ?? ''}:${userId ?? ''}`;
  const contextKeyRef = useRef(contextKey);
  const { data, error, isLoading: loading, refetch } = useBaseTemplates(organizationId, userId);

  contextKeyRef.current = contextKey;
  onCloseRef.current = onClose;

  useEffect(() => {
    setAllTemplatesMarketId(data.accessibleMarkets.find((market) => market.hasLicense)?.id
      ?? (data.accessibleMarkets.length === 1 ? data.accessibleMarkets[0].id : null));
  }, [organizationId, userId, data.accessibleMarkets]);

  useEffect(() => {
    selectionRequestId.current += 1;
    setSelectingTemplateId(null);
    setSelectionError('');
    return () => {
      selectionRequestId.current += 1;
    };
  }, [contextKey]);

  const visibleTemplates = useMemo(() => data.templates.filter((template) => {
    return allTemplatesMarketId === null || template.marketIds.includes(allTemplatesMarketId);
  }), [allTemplatesMarketId, data.templates]);

  useEffect(() => {
    if (step === 'source') backButtonRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const focusableSelector = 'button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    dialogRef.current?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      const activeDialog = dialogRef.current;
      if (e.key !== 'Tab' || !activeDialog) return;
      const focusable = Array.from(
        activeDialog.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => !element.hasAttribute('disabled'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!(document.activeElement instanceof Node) || !activeDialog.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && (document.activeElement === first || document.activeElement === activeDialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  const handleUseTemplate = async (catalogTemplate: BaseTemplate) => {
    if (!organizationId || !selectedFormat || selectingTemplateId !== null) return;
    const requestId = ++selectionRequestId.current;
    const requestedContext = contextKeyRef.current;
    setSelectingTemplateId(catalogTemplate.id);
    setSelectionError('');
    try {
      const validatedTemplate = await getBaseTemplateById({
        organizationId,
        templateId: catalogTemplate.id,
      });
      if (requestId !== selectionRequestId.current || requestedContext !== contextKeyRef.current) return;
      if (validatedTemplate.id !== catalogTemplate.id) {
        setSelectionError('The selected Base Template could not be validated. Please choose another template.');
        return;
      }
      const parsed = prepareBaseTemplateSource(validatedTemplate);
      if (!parsed
        || !Number.isFinite(parsed.width)
        || parsed.width <= 0
        || !Number.isFinite(parsed.height)
        || parsed.height <= 0
        || validateTemplateForSave(parsed).length > 0) {
        setSelectionError('The selected Base Template contains invalid design data. Please choose another template.');
        return;
      }
      const accessibleMarketIds = new Set(data.accessibleMarkets.map((market) => market.id));
      const sourceMarketIds = validatedTemplate.marketIds.filter(
        (marketId) => accessibleMarketIds.has(marketId),
      );
      onSelectTemplate(
        parsed,
        `${validatedTemplate.name} - Copy`,
        selectedFormat,
        sourceMarketIds,
        {
          market: data.accessibleMarkets.filter((market) => sourceMarketIds.includes(market.id)).map((market) => market.name).join(', '),
          category: validatedTemplate.category || catalogTemplate.category,
        },
      );
    } catch (selectionFailure) {
      if (requestId !== selectionRequestId.current || requestedContext !== contextKeyRef.current) return;
      console.error('Failed to load selected Base Template:', selectionFailure);
      setSelectionError('The selected Base Template is no longer available. Please choose another template.');
    } finally {
      if (requestId === selectionRequestId.current && requestedContext === contextKeyRef.current) {
        setSelectingTemplateId(null);
      }
    }
  };

  const handleFormatSelect = (format: OutputFormat, dpi: number) => {
    setSelectedFormat({ format, dpi });
    setStep('source');
  };

  const handleAllTemplatesMarketChange = (marketId: number | null) => {
    setAllTemplatesMarketId(marketId);
    setSelectionError('');
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="new-template-chooser"
      onMouseDown={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 20000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={step === 'format' ? 'New Template' : 'Choose a starting point'}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          width: step === 'source' ? 960 : 820,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid var(--border, #e2e5e9)',
        }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            {step === 'format' ? 'New Template' : 'Choose a starting point'}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {step === 'source' && !initialFormat && (
              <button
                ref={backButtonRef}
                className="subtle-btn"
                onClick={() => setStep('format')}
                disabled={selectingTemplateId !== null}
                style={{ fontSize: 13, padding: '4px 8px' }}
              >
                Back
              </button>
            )}
            <button
              className="subtle-btn"
              onClick={onClose}
              aria-label="Close"
              data-testid="new-template-chooser-close"
              style={{ fontSize: 18, lineHeight: 1, padding: 4 }}
            >
              &times;
            </button>
          </div>
        </div>

        <div className={step === 'source' ? 'template-chooser-body' : undefined} style={step === 'format' ? { padding: '16px 20px', overflowY: 'auto', flex: 1 } : undefined}>
          {step === 'format' && (
            <>
              <div style={{ marginBottom: 12, fontSize: 14, color: '#5f6b7a' }}>
                Choose output format (this cannot be changed later):
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {getFormatChooserOptions().map((opt) => (
                  <FormatCard
                    key={`${opt.format}-${opt.dpi}`}
                    title={`${opt.displayName} + CSV${opt.recommended ? ' (Recommended/Default)' : ` (${opt.dpi} DPI)`}`}
                    description={opt.dpiLabel}
                    note={opt.format === 'zpl' ? 'Requires ZPL printer' : undefined}
                    testId={`format-${opt.format}-${opt.dpi}`}
                    recommended={opt.recommended}
                    onClick={() => handleFormatSelect(opt.format, opt.dpi)}
                  />
                ))}
              </div>
            </>
          )}

          {step === 'source' && selectedFormat && (
            <>
              <div className="template-chooser-format">
                Format: <strong>{selectedFormat.format.toUpperCase()} + CSV ({selectedFormat.dpi} DPI)</strong>
              </div>

              <button
                type="button"
                onClick={() => onSelectBlank(selectedFormat)}
                disabled={selectingTemplateId !== null}
                data-testid="new-template-blank"
                className="template-chooser-blank"
              >
                <span className="template-chooser-blank-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33301 8H12.6663" stroke="#213F39" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M8 3.33301V12.6663" stroke="#213F39" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div>
                  <div className="template-chooser-row-title">Blank template</div>
                  <div className="template-chooser-description">Start from scratch with an empty canvas.</div>
                </div>
              </button>

              <section className="template-chooser-templates" aria-labelledby="base-templates-heading">
                <h2 id="base-templates-heading">Base templates</h2>

                {!loading && !error && data.accessibleMarkets.length > 0 && (
                  <label className="template-chooser-market-filter">
                    Market
                    <select
                      className="input"
                      aria-label="Filter base templates by market"
                      disabled={selectingTemplateId !== null}
                      value={allTemplatesMarketId ?? ''}
                      onChange={(event) => handleAllTemplatesMarketChange(
                        event.target.value ? Number(event.target.value) : null,
                      )}
                    >
                      <option value="">All markets</option>
                      {data.accessibleMarkets.map((market) => (
                        <option key={market.id} value={market.id}>{market.name}</option>
                      ))}
                    </select>
                  </label>
                )}

                {loading && (
                  <div className="template-chooser-state muted">
                    Loading templates...
                  </div>
                )}

                {!loading && error && (
                  <div className="template-chooser-state" role="alert">
                    <div>Base templates could not be loaded.</div>
                    <button type="button" className="subtle-btn" onClick={() => refetch()}>
                      Try again
                    </button>
                  </div>
                )}

                {selectionError && (
                  <div role="alert" className="template-chooser-error">
                    {selectionError}
                  </div>
                )}

                {!loading && !error && data.templates.length === 0 && (
                  <div className="template-chooser-state muted">
                    <div>No base templates are currently available.</div>
                  </div>
                )}

                {!loading && !error
                  && allTemplatesMarketId !== null && visibleTemplates.length === 0
                  && data.templates.length > 0 && (
                  <div className="template-chooser-empty muted">
                    <div>No base templates are available for this market.</div>
                    <button
                      type="button"
                      className="subtle-btn"
                      onClick={() => handleAllTemplatesMarketChange(null)}
                    >
                      View all markets
                    </button>
                  </div>
                )}

                {!loading && !error && visibleTemplates.length > 0 && (
                  <ul className="template-chooser-list" aria-label="Base templates">
                    {visibleTemplates.map((template) => {
                      const isSelecting = selectingTemplateId === template.id;
                      const title = template.name.replace(/^Base\s*-\s*[^-]+\s*-\s*/i, '') || template.name;
                      const marketNames = template.markets
                        .filter((market) => !titleContainsMetadata(title, market.name))
                        .map((market) => market.name).join(', ');
                      return (
                        <li
                          key={template.id}
                          data-testid={`new-template-base-${template.id}`}
                        >
                          <button
                            type="button"
                            className={`template-chooser-row template-chooser-card${isSelecting ? ' selected' : ''}`}
                            aria-label={`Open ${template.name}`}
                            aria-busy={isSelecting}
                            disabled={selectingTemplateId !== null}
                            onClick={() => handleUseTemplate(template)}
                          >
                            <span className="template-chooser-row-copy">
                              <span className="template-chooser-row-title">{title}</span>
                              {template.category && !titleContainsMetadata(title, template.category) && <span className="template-chooser-category">{template.category}</span>}
                              {marketNames && <span className="template-chooser-markets">{marketNames}</span>}
                              <span className="template-chooser-metadata">{getTemplateMetadata(template)}</span>
                            </span>
                            {isSelecting && <span role="status">Opening...</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

              </section>
            </>
          )}
        </div>

      </div>

    </div>,
    document.body
  );
}
